import moment from "moment-timezone"
import {Organisation} from "./organisation.model";
import {User} from "../../shared/user/user.model";
import {FileEntry} from "../../../app/components/uploader/file-entry.model";
import {environment} from "../../../app/config/environment";
import {JobChange} from "./job-change.model";

export class Job {
  id: number;
  starts_at:string;
  ends_at:string;
  latitude:any;
  longitude:any;
  address:string;
  address_details:string;
  language1_id: number = 1; // Danish
  language1: any = {name: 'Dansk', id: 1};
  language2_id: number;
  language2: any;
  interpreter_id: number;
  description:string;
  interpreter: any;
  client_user_id: number;
  publisher: User;
  preferred_interpreter_id: number;
  preferred_interpreter: any;
  undesirable_interpreters: any[];
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  status: string;
  interpreter_base_wage: number;
  created_at: string;
  updated_at: string;
  finished_at: string;
  rating: number;
  review: string;
  required_gender: string = null;
  editMode: boolean = false;
  edited: boolean = false;
  cancelled_at: string;
  cancellation_reason: string;
  cancelled_by: number;
  cancelled_by_user: User;
  rejected_at: string;
  rejection_reason: string;
  interpretation_receiver_birthdate: string;
  interpretation_receiver_name: string;
  organisation: Organisation;
  type: string = 'simultaneous';
  remote_contact_info: string;
  file_entries: FileEntry[];
  timezone: string;
  bonus: number;
  changes: JobChange[];
  accepted_at: string;
  participants: any[];
  signatures: any[];
  specialities: any[];

  constructor(data?){
    if(data){
      this.id = data.id;
      this.description = data.description;
      this.latitude = data.latitude;
      this.longitude = data.longitude;
      this.address = data.address;
      this.address_details = data.address_details;
      this.language1_id = data.language1_id;
      this.language1 = data.language1;
      this.language2_id = data.language2_id;
      this.language2 = data.language2;
      this.interpreter_id = data.interpreter_id;
      this.interpreter = data.interpreter;
      this.client_user_id = data.client_user_id;
      this.publisher = data.publisher;
      this.preferred_interpreter_id = data.preferred_interpreter_id;
      this.preferred_interpreter = data.preferred_interpreter;
      this.undesirable_interpreters = data.undesirable_interpreters;
      this.contact_name = data.contact_name;
      this.contact_phone = data.contact_phone;
      this.contact_email = data.contact_email;
      this.status = data.status;
      this.rating = data.rating;
      this.review = data.review;
      this.required_gender = data.required_gender;
      this.interpreter_base_wage = data.interpreter_base_wage;
      this.cancellation_reason = data.cancellation_reason;
      this.cancelled_by = data.cancelled_by;
      this.cancelled_by_user = data.cancelled_by_user;
      this.rejection_reason = data.rejection_reason;
      this.interpretation_receiver_birthdate = data.interpretation_receiver_birthdate;
      this.interpretation_receiver_name = data.interpretation_receiver_name;
      this.organisation = data.organisation;
      this.type = data.type;
      this.remote_contact_info = data.remote_contact_info;
      this.file_entries = data.file_entries;
      this.timezone = data.timezone;
      this.bonus = data.bonus;
      this.changes = data.changes;
      this.participants = data.participants ? data.participants : [];
      this.signatures = data.signatures ? data.signatures : [];
      this.specialities = data.specialities ? data.specialities : [];

      // Set the dates
      this.starts_at = moment(data.starts_at).format();
      this.updated_at = moment(data.updated_at).format();
      this.created_at = moment(data.created_at).format();

      if(this.typeHasEndTime()){
        this.ends_at = moment(data.ends_at).format();
      }

      if(data.finished_at){
        this.finished_at = moment(data.finished_at).format();
      }

      if(data.cancelled_at){
        this.cancelled_at = moment(data.cancelled_at).format();
      }

      if(data.rejected_at){
        this.rejected_at = moment(data.rejected_at).format();
      }

      if(data.accepted_at){
        this.accepted_at = moment(data.accepted_at).format();
      }
    }else{
      this.starts_at = moment({date: moment().date() + 1,minutes: 0, hours: 8}).format();
      this.ends_at = moment({minutes: 0, hours: 12}).format();
      this.file_entries = [];
      this.participants = [];
      this.signatures = [];
      this.undesirable_interpreters = [];
      this.specialities = [];
    }
  }

  isDraft(){
    return !this.status || this.status === 'draft';
  }

  isEditable(){
    return this.editMode;
  }

  edit(){
    this.editMode = true;
    this.edited = true;
  }

  /**
   * If the job has been accepted at some point by an interpreter
   * Not the same as if the job has been "preferred by an interpreter"
   * @returns {number}
   */
  isAssigned(){
    return this.interpreter_id;
  }


  isNew(){
    return !this.id;
  }

  isFinished(){
    return this.status === 'cancelled'
      || this.status === 'expired'
      || this.status === 'rejected'
      || this.status === 'done';
  }

  canBeSaved(){
    return this.edited;
  }

  canTurnOnEditMode(){
    return !this.isEditable()
      && !this.isFinished() && !this.canBeUnpublished();
  }

  canBeUnpublished(){
    return !this.isDraft() && !this.isAssigned() && !this.isFinished();
  }

  canBeRepublished(){
    // Cancelled or rejected by preferred interpreter
    return (this.status === 'cancelled' && this.preferred_interpreter_id === this.cancelled_by)
      || this.status === 'rejected';
  }

  canBeRepublishedAsNew(){
    // Cancelled by client
    // Or expired, done
    return (this.status === 'cancelled' && this.cancelled_by !== this.interpreter_id)
      || this.status === 'expired'
      || this.status === 'done';
  }


  canBeDeleted(){
    return this.isDraft() && !this.isNew();
  }

  canBeCancelled(){
    return this.isAssigned() && !this.isFinished() && !this.isNew();
  }

  isReviewable(){
    return this.status === 'done' || this.status === 'cancelled' || this.status === 'rejected';
  }

  canBeRejected(){
    return !this.isAssigned() && this.preferred_interpreter_id && this.status !== 'rejected';
  }

  canBeAccepted(){
    return !this.isAssigned() && this.status !== 'rejected';
  }

  typeHasLocation(){
    return this.type === 'simultaneous' || this.type === 'mentorship';
  }

  typeHasEndTime(){
    return this.type !== 'written';
  }

  typeHasRemoteContactInfo(){
    return this.type === 'phone';
  }

  endsAtIsInFuture(){
    return moment(this.ends_at) > moment();
  }

  attachmentsCanBeSubmitted(){
    return this.type === 'written' && this.status === 'accepted';
  }

  hasBaseWage(){
    return this.type !== 'written';
  }

  getLocationBackgroundImage(){
    if(!this.address){
      return null;
    }
    return 'https://maps.googleapis.com/maps/api/streetview?size=600x300&location=' + this.address + '&key=' + environment.google.key;
  }

  hasVideoLink(){
    return this.type === 'video' && this.isAssigned() && !this.isEditable() && !this.isFinished() && this.status !== 'pending';
  }
}



// WEBPACK FOOTER //
// ./src/pages/job/shared/job.model.ts